import { z } from "zod";

import { COGNITO_ALL_PROFILE_GROUPS, COGNITO_INTERNAL_STAFF_GROUPS } from "@recobra/core";

import type { SystemTaskKey } from "../task";
import { systemTaskType } from "./system-task-types";

export const TaskAssignmentSchema = z.discriminatedUnion("mode", [
  z.object({ mode: z.literal("unassigned") }).strict(),
  z.object({ mode: z.literal("user"), username: z.string().trim().min(1) }).strict(),
  z.object({ mode: z.literal("previous_assignee") }).strict(),
  z.object({ mode: z.literal("source_creator") }).strict(),
]);
export type TaskAssignment = z.infer<typeof TaskAssignmentSchema>;

export const TaskProfileGroupsSchema = z
  .array(z.enum(COGNITO_ALL_PROFILE_GROUPS))
  .refine((groups) => new Set(groups).size === groups.length)
  .describe("Profiles that may receive the task; empty means any eligible profile");

/** The automatic assignment rules a task type can use. */
export const taskAssignmentModes = ({
  scope,
  system,
}: {
  scope: string;
  system: SystemTaskKey | null;
}): readonly TaskAssignment["mode"][] =>
  systemTaskType(system)?.assignmentModes ??
  (scope === "client" ? ["unassigned", "user"] : ["unassigned", "user", "previous_assignee"]);

const ORDINARY_TASK_PROFILES = COGNITO_ALL_PROFILE_GROUPS.filter((group) => group !== "Customer");
const CUSTOMER_TASK_PROFILES = [...COGNITO_INTERNAL_STAFF_GROUPS, "Customer" as const];

/** The profiles that can ever receive a task of this type. */
export const taskAssigneeProfiles = (
  system: SystemTaskKey | null,
): readonly (typeof COGNITO_ALL_PROFILE_GROUPS)[number][] =>
  systemTaskType(system)?.customerAssignable ? CUSTOMER_TASK_PROFILES : ORDINARY_TASK_PROFILES;
